import { useEffect, useRef } from "react";

// Calls `fn` every `intervalMs` while `enabled` AND the tab is visible. Pauses
// when the tab is backgrounded and fires once immediately when it returns
// (so a returning user sees fresh data at once). Used to keep the realtime
// dashboards live without a manual reload — the backend produces a new sample
// roughly every 10 min, so the caller should poll faster than that but only in
// "live" mode (date range open-ended = up to now), never for a fixed historical
// window. The poll should be SILENT (no loading spinner) — pass a fetch that
// swaps data in without toggling loading state.
export function usePollingRefresh(
  fn: () => void,
  intervalMs: number,
  enabled: boolean
): void {
  const fnRef = useRef(fn);
  fnRef.current = fn; // always call the latest closure without resetting the timer

  useEffect(() => {
    if (!enabled) return;
    let id: ReturnType<typeof setInterval> | null = null;
    const start = () => {
      if (id == null) id = setInterval(() => fnRef.current(), intervalMs);
    };
    const stop = () => {
      if (id != null) {
        clearInterval(id);
        id = null;
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        fnRef.current(); // refresh immediately on return
        start();
      } else {
        stop();
      }
    };
    if (document.visibilityState === "visible") start();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [enabled, intervalMs]);
}
